/****************************************************************************
 * Copyright (C) 2017 ecsec GmbH.
 * All rights reserved.
 * Contact: ecsec GmbH (info@ecsec.de)
 *
 * This file is part of SkIDentity.
 *
 * This file may be used in accordance with the terms and conditions
 * contained in a signed written agreement between you and ecsec GmbH.
 *
 ***************************************************************************/

import sjcl = require("@ecsec/sjcl");


class Sjcl2JWK {
	static curveName(val: string) : string {
		switch(val) {
			case "c256": return "P-256";
			case "c384": return "P-384";
			case "c521": return "P-521";
			default:
				throw "Unsupported SJCL curve type: " + val;
		}
	}
}

class JWK2Sjcl {
	static curveName(val: string) : string {
		switch(val) {
			case "P-256": return "c256";
			case "P-384": return "c384";
			case "P-521": return "c521";
			default:
				throw "Unsupported JWK curve type: " + val;
		}
	}
}


export abstract class BaseKey {
	protected constructor(protected readonly keyType: string) {
	}
}

export interface ExportedECKey {
	kty: string;
	crv: string;
	x: string;
	y: string;
}

export class ECKey extends BaseKey {

	private crv: string;
	private x: string;
	private y: string;

	constructor() {
		super("EC");
	}

	public loadSjclPubKey(pubKey: sjcl.SjclECCPublicKey) {
		this.crv = Sjcl2JWK.curveName(pubKey.curveName());
		let rawKey = pubKey.get();
		this.x = sjcl.codec.base64url.fromBits(rawKey.x);
		this.y = sjcl.codec.base64url.fromBits(rawKey.y);
		return this;
	}

	public loadJWK(pubKey: ExportedECKey) {
		this.crv = pubKey.crv;
		this.x = pubKey.x;
		this.y = pubKey.y;
		return this;
	}

	public exportJWK(): ExportedECKey {
		return {
			kty: this.keyType,
			crv: this.crv,
			x: this.x,
			y: this.y
		};
	}

	public exportSjcl(keyType: "elGamal" | "ecdsa") : sjcl.SjclECCPublicKeySerialized {
		let xRaw = sjcl.codec.base64url.toBits(this.x);
		let yRaw = sjcl.codec.base64url.toBits(this.y);
		let xy = sjcl.bitArray.concat(xRaw, yRaw);

		return {
			type: keyType,
			secretKey: false,
			point: sjcl.codec.hex.fromBits(xy),
			curve: JWK2Sjcl.curveName(this.crv)
		};
	}

}



// WEBPACK FOOTER //
// ./node_modules/@ecsec/base-libs/src/pairing/jwk.ts